import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import APIService from "../../../../services/APIService";
import { useCreateSection } from "../../../TutorPage/Dashboard/hooks/useDashboard";
import type { Section, CourseCardData, TabType, SortType } from "../types";
import {
	transformSectionData,
	transformManagingSection,
	extractEnrollmentCode,
} from "../utils/sectionUtils";

export function useClassPage() {
	const navigate = useNavigate();
	const { user, isAuthenticated } = useAuth();
	const [enrolledSections, setEnrolledSections] = useState<Section[]>([]);
	const [managingSections, setManagingSections] = useState<CourseCardData[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<TabType>("all");
	const [searchTerm, setSearchTerm] = useState("");
	const [sortBy, setSortBy] = useState<SortType>("recent");
	const [enrollmentCode, setEnrollmentCode] = useState("");
	const [enrollLoading, setEnrollLoading] = useState(false);
	const [showEnrollModal, setShowEnrollModal] = useState(false);

	const userName = user?.name || user?.username || user?.email || "사용자 이름";

	const fetchEnrolledSections = useCallback(async () => {
		if (!isAuthenticated) {
			setLoading(false);
			return;
		}
		try {
			setLoading(true);
			setError(null);
			const response = await APIService.getUserEnrolledSections();
			setEnrolledSections(response.data || response);
		} catch (err: unknown) {
			console.error("수강 중인 section 조회 실패:", err);
			setError(
				err instanceof Error ? err.message : "수강 중인 강의를 불러오는데 실패했습니다.",
			);
		} finally {
			setLoading(false);
		}
	}, [isAuthenticated]);

	const fetchManagingSections = useCallback(async () => {
		if (!isAuthenticated) return;
		try {
			const response = await APIService.getManagingSections();
			const sectionsData = response?.data ?? response ?? [];
			const transformed = Array.isArray(sectionsData)
				? sectionsData.map(transformManagingSection)
				: [];
			setManagingSections(transformed);
		} catch (err) {
			console.error("관리 중인 수업 조회 실패:", err);
			setManagingSections([]);
		}
	}, [isAuthenticated]);

	useEffect(() => {
		fetchEnrolledSections();
	}, [fetchEnrolledSections]);

	useEffect(() => {
		if (isAuthenticated) {
			fetchManagingSections();
		}
	}, [isAuthenticated, fetchManagingSections]);

	// 수업 만들기: 관리 페이지로 이동하지 않고 이 페이지에서 바로 모달로 생성
	const createSection = useCreateSection(async () => {
		await fetchManagingSections();
		setActiveTab("in-progress");
	});

	const handleStatusUpdate = useCallback(async () => {
		try {
			const response = await APIService.getUserEnrolledSections();
			setEnrolledSections(response.data || response);
			fetchManagingSections();
		} catch (err) {
			console.error("대시보드 새로고침 실패:", err);
		}
	}, [fetchManagingSections]);

	const getFilteredSections = useCallback((): CourseCardData[] => {
		let list: CourseCardData[];
		if (activeTab === "all") {
			list = enrolledSections.map(transformSectionData);
		} else if (activeTab === "in-progress") {
			list = [...managingSections];
		} else {
			list = [];
		}
		if (searchTerm) {
			list = list.filter(
				(section) =>
					section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
					section.courseName.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}
		if (sortBy === "recent") {
			list.sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);
		} else if (sortBy === "name") {
			list.sort((a, b) => a.title.localeCompare(b.title));
		}
		return list;
	}, [enrolledSections, managingSections, activeTab, searchTerm, sortBy]);

	const stats = useMemo(() => {
		return {
			all: enrolledSections.length,
			inProgress: managingSections.length,
			completed: 0,
		};
	}, [enrolledSections, managingSections]);

	const filteredSections = useMemo(
		() => getFilteredSections(),
		[getFilteredSections],
	);

	const managingSectionIds = useMemo(
		() => new Set(managingSections.map((s) => s.sectionId)),
		[managingSections],
	);

	// 내가 관리하는 수업(교수·튜터)은 어느 탭에서 눌러도 해당 수업의 관리 화면으로 이동
	const getCourseLinkPath = useCallback(
		(course: CourseCardData): string | undefined => {
			if (!course.sectionId) return undefined;
			if (
				activeTab === "in-progress" ||
				managingSectionIds.has(course.sectionId)
			) {
				return `/tutor/assignments/section/${course.sectionId}`;
			}
			return undefined;
		},
		[activeTab, managingSectionIds],
	);

	const handleEnrollByCode = useCallback(async () => {
		if (!enrollmentCode.trim()) {
			alert("참가 코드를 입력하세요.");
			return;
		}
		const code = extractEnrollmentCode(enrollmentCode);
		if (!code) {
			alert("유효한 참가 코드나 링크를 입력하세요.");
			return;
		}
		try {
			setEnrollLoading(true);
			const resp = await APIService.enrollByCode(code);
			if (resp && resp.success) {
				alert(`${resp.courseTitle} 수강 신청이 완료되었습니다!`);
				setEnrollmentCode("");
				const refreshed = await APIService.getUserEnrolledSections();
				setEnrolledSections(refreshed.data || refreshed);
			} else {
				alert(resp?.message || "수강 신청에 실패했습니다.");
			}
		} catch (e: unknown) {
			alert(e instanceof Error ? e.message : "수강 신청 중 오류가 발생했습니다.");
		} finally {
			setEnrollLoading(false);
		}
	}, [enrollmentCode]);

	return {
		navigate,
		userName,
		loading,
		error,
		activeTab,
		setActiveTab,
		searchTerm,
		setSearchTerm,
		sortBy,
		setSortBy,
		enrollmentCode,
		setEnrollmentCode,
		enrollLoading,
		showEnrollModal,
		setShowEnrollModal,
		stats,
		filteredSections,
		getCourseLinkPath,
		handleStatusUpdate,
		handleEnrollByCode,
		createSection,
	};
}

export type ClassPageHookReturn = ReturnType<typeof useClassPage>;
